import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { User, Smartphone, Mail, Link2, Lock, Eye, EyeOff, Lightbulb, Loader2, Rocket } from 'lucide-react';

export default function Register() {
  const { refCode } = useParams();
  const [form, setForm] = useState({
    name: '', email: '', mobile: '',
    password: '', confirmPassword: '',
    sponsorCode: refCode || ''
  });
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (refCode) setForm(f => ({ ...f, sponsorCode: refCode }));
  }, [refCode]);

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.mobile || !form.password) return toast.error('Please fill all required fields');
    if (!form.sponsorCode || !form.sponsorCode.trim()) return toast.error('A referral code is required to register');
    if (form.password !== form.confirmPassword) return toast.error('Passwords do not match');
    if (form.password.length < 6) return toast.error('Password must be at least 6 characters');
    if (!/^\d{10}$/.test(form.mobile)) return toast.error('Enter valid 10-digit mobile number');

    setLoading(true);
    try {
      const { confirmPassword, ...data } = form;
      const user = await register(data);
      toast.success(`Welcome, ${user.name?.split(' ')[0]}! Registration successful.`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="auth-page">
        <div className="auth-card" style={{ maxWidth: 520 }}>
          <div className="auth-logo">
            <img src="/logo.png" alt="Triveni Pay" className="auth-logo-img" />
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', letterSpacing: '2px', marginTop: 4 }}>FOREX INVESTMENT PLATFORM</div>
          </div>
          <h2 className="auth-title">Create Account</h2>
          <p className="auth-subtitle">Join us and start your investment journey</p>

          <form onSubmit={handleSubmit}>
            <div className="grid-2" style={{ gap: '0 16px' }}>
              <div className="form-group">
                <label>Full Name *</label>
                <div className="input-group">
                  <span className="input-icon"><User size={16} /></span>
                  <input type="text" name="name" className="form-control" placeholder="Your full name" value={form.name} onChange={handleChange} required />
                </div>
              </div>
              <div className="form-group">
                <label>Mobile Number *</label>
                <div className="input-group">
                  <span className="input-icon"><Smartphone size={16} /></span>
                  <input type="tel" name="mobile" className="form-control" placeholder="10-digit mobile" value={form.mobile} onChange={handleChange} required maxLength={10} />
                </div>
              </div>
            </div>
            <div className="form-group">
              <label>Email Address *</label>
              <div className="input-group">
                <span className="input-icon"><Mail size={16} /></span>
                <input type="email" name="email" className="form-control" placeholder="Your email address" value={form.email} onChange={handleChange} required />
              </div>
            </div>
            <div className="form-group">
              <label>Referral Code *</label>
              <div className="input-group">
                <span className="input-icon"><Link2 size={16} /></span>
                <input
                  type="text"
                  name="sponsorCode"
                  className="form-control"
                  placeholder="Enter your sponsor's referral code"
                  value={form.sponsorCode}
                  onChange={handleChange}
                  style={{ textTransform: 'uppercase' }}
                  readOnly={!!refCode}
                  required
                />
              </div>
              {refCode ? (
                <small style={{ color: '#22c55e', fontSize: '0.78rem' }}>✓ Referral code applied from your invite link</small>
              ) : (
                <small style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>You need a referral code from an existing member to join — registration isn't open without one.</small>
              )}
            </div>
            <div className="grid-2" style={{ gap: '0 16px' }}>
              <div className="form-group">
                <label>Password *</label>
                <div className="input-group" style={{ position: 'relative' }}>
                  <span className="input-icon"><Lock size={16} /></span>
                  <input type={showPass ? 'text' : 'password'} name="password" className="form-control" placeholder="Min 6 characters" value={form.password} onChange={handleChange} required style={{ paddingRight: 44 }} />
                  <button type="button" onClick={() => setShowPass(!showPass)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>Confirm Password *</label>
                <div className="input-group">
                  <span className="input-icon"><Lock size={16} /></span>
                  <input type={showPass ? 'text' : 'password'} name="confirmPassword" className="form-control" placeholder="Repeat password" value={form.confirmPassword} onChange={handleChange} required />
                </div>
              </div>
            </div>

            <div className="alert alert-info" style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <Lightbulb size={16} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>By registering, you agree to Triveni Pay's terms and conditions. Package: Min ₹500 - Max ₹5,00,000 (multiples of ₹500).</span>
            </div>

            <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={loading}>
              {loading ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Loader2 size={16} className="spin" /> Creating Account...
                </span>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Rocket size={16} /> Create Account
                </span>
              )}
            </button>
          </form>

          <div className="auth-footer" style={{ marginTop: 16 }}>
            Already have an account? <Link to="/login">Sign In</Link>
          </div>
        </div>
      </div>
    </>
  );
}